import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../../api/useFetchObjects";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Grid,
  Chip,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Visibility as VisibilityIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import moment from "moment";

export default function QueueList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const [queueDate, setQueueDate] = useState(moment().format("YYYY-MM-DD"));
  const [doctorFilter, setDoctorFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const queryParams = new URLSearchParams();
  queryParams.set("queue_date", queueDate);
  if (doctorFilter) queryParams.set("doctor", doctorFilter);
  if (departmentFilter) queryParams.set("department", departmentFilter);
  if (statusFilter) queryParams.set("status", statusFilter);

  const { data, isLoading, isError, error, refetch } = useFetchObjects(
    ["queue-entries", queueDate, doctorFilter, departmentFilter, statusFilter],
    `queue-entries/?${queryParams.toString()}&page_size=200`,
    token,
    { refetchInterval: 30000 } // Auto-refresh every 30s for real-time feel
  );

  const doctorsUrl = departmentFilter
    ? `users/?doctor=true&department=${departmentFilter}&page_size=500`
    : "users/?doctor=true&page_size=500";
  const { data: doctorsData } = useFetchObjects(
    ["doctors-queue", departmentFilter],
    doctorsUrl,
    token
  );
  const doctors = doctorsData?.results || doctorsData || [];

  const { data: departmentsData } = useFetchObjects(
    ["departments"],
    "departments/",
    token
  );
  const departments = departmentsData?.results || departmentsData || [];

  const entries = data?.results || data || [];

  const getStatusColor = (status) => {
    switch (Number(status)) {
      case 1:
        return "default";
      case 2:
        return "primary";
      case 3:
        return "success";
      case 4:
        return "error";
      default:
        return "default";
    }
  };

  const handleViewProfile = (ticketId) => {
    navigate(`/patient-profile/ticket/${ticketId}`);
  };

  if (isError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error?.message || "Failed to load queue"}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, mb: 2 }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              {t("reception.opdQueue", { defaultValue: "OPD Queue" })}
            </Typography>
          </Grid>
          <Grid item xs={12} md={8}>
            <Grid container spacing={2} justifyContent="flex-end">
              <Grid item>
                <TextField
                  size="small"
                  type="date"
                  label={t("reception.date")}
                  value={queueDate}
                  onChange={(e) => setQueueDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 160 }}
                />
              </Grid>
              <Grid item>
                <TextField
                  select
                  size="small"
                  label={t("reception.doctor")}
                  value={doctorFilter}
                  onChange={(e) => setDoctorFilter(e.target.value)}
                  sx={{ minWidth: 180 }}
                >
                  <MenuItem value="">{t("reception.allDoctors", { defaultValue: "All Doctors" })}</MenuItem>
                  {doctors.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.first_name} {d.last_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item>
                <TextField
                  select
                  size="small"
                  label={t("reception.department")}
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="">{t("reception.allDepartments", { defaultValue: "All" })}</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item>
                <TextField
                  select
                  size="small"
                  label={t("reception.status")}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{ minWidth: 130 }}
                >
                  <MenuItem value="">{t("reception.all", { defaultValue: "All" })}</MenuItem>
                  <MenuItem value="1">{t("reception.waiting", { defaultValue: "Waiting" })}</MenuItem>
                  <MenuItem value="2">{t("reception.inProgress", { defaultValue: "In Progress" })}</MenuItem>
                  <MenuItem value="3">{t("reception.completed", { defaultValue: "Completed" })}</MenuItem>
                  <MenuItem value="4">{t("reception.cancelled", { defaultValue: "Cancelled" })}</MenuItem>
                </TextField>
              </Grid>
              <Grid item>
                <Tooltip title={t("reception.refresh", { defaultValue: "Refresh" })}>
                  <IconButton onClick={() => refetch()} color="primary">
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.200" }}>
                  <TableCell><strong>#</strong></TableCell>
                  <TableCell><strong>{t("reception.ticketNumber")}</strong></TableCell>
                  <TableCell><strong>{t("reception.patient")}</strong></TableCell>
                  <TableCell><strong>{t("reception.doctor")}</strong></TableCell>
                  <TableCell><strong>{t("reception.department")}</strong></TableCell>
                  <TableCell><strong>{t("reception.status")}</strong></TableCell>
                  <TableCell align="right"><strong>{t("reception.action")}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      {t("reception.noRecords", { defaultValue: "No queue entries for this filter." })}
                    </TableCell>
                  </TableRow>
                ) : (
                  entries.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.queue_number}</TableCell>
                      <TableCell>{row.ticket_number_display || "—"}</TableCell>
                      <TableCell>{row.patient_name_display || "—"}</TableCell>
                      <TableCell>{row.doctor_name_display || "—"}</TableCell>
                      <TableCell>{row.department_name_display || "—"}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.status_display}
                          color={getStatusColor(row.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewProfile(row.ticket)}
                        >
                          {t("reception.viewProfile", { defaultValue: "View Profile" })}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
